import React, { useState } from 'react';
import { Proposal, ProposalStatus } from '../api/proposalApi';
import { Eye, Send, Pencil, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import ProposalStatusBadge from './ProposalStatusBadge';
import Spinner from './Spinner';

const ITEMS_PER_PAGE = 10;

interface Props {
  proposals: Proposal[];
  loading: boolean;
  onSend: (id: string) => void;
  onView: (id: string) => void;
  onEdit: (proposal: Proposal) => void;
  onDelete: (id: string) => void;
}

export default function ProposalsList({ proposals, loading, onSend, onView, onEdit, onDelete }: Props) {
  const [currentPage, setCurrentPage] = useState(1);

  const getPaginatedProposals = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return proposals.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(proposals.length / ITEMS_PER_PAGE);
  };

  const paginatedData = getPaginatedProposals();
  const totalPages = getTotalPages();

  return (
    <>
      <div className="bg-white rounded-t-[2rem] border border-b-0 border-strivre-lightnavy overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-12 flex flex-col items-center justify-center"><Spinner /></div>
          ) : (
            <div className="overflow-x-auto border-t border-strivre-lightnavy">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-strivre-softgrey border-b border-strivre-lightnavy text-xs font-bold text-slate-500 uppercase tracking-wider">
                    <th className="px-6 py-4 border-r border-strivre-lightnavy">Title</th>
                    <th className="px-6 py-4 border-r border-strivre-lightnavy">Customer</th>
                    <th className="px-6 py-4 border-r border-strivre-lightnavy text-center">Status</th>
                    <th className="px-6 py-4 border-r border-strivre-lightnavy">Expiry Date</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="font-medium">
                  {paginatedData.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-6 py-4 text-slate-900 border-r border-b border-slate-100">{p.title}</td>
                      <td className="px-6 py-4 border-r border-b border-slate-100">
                        <div className="text-sm text-slate-700">{p.customerName}</div>
                        <div className="text-[12px] text-slate-400 font-medium">{p.customerEmail}</div>
                      </td>
                      <td className="px-6 py-4 text-center border-r border-b border-slate-100"><ProposalStatusBadge status={p.status} /></td>
                      <td className="px-6 py-4 text-sm text-slate-500 border-r border-b border-slate-100">
                        {p.expiryDate ? new Date(p.expiryDate).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 border-b border-slate-100">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => onView(p.id)} className="p-2 bg-slate-50 text-slate-500 rounded-lg hover:text-strivre-burgundy border border-transparent hover:border-strivre-lightnavy transition-all">
                            <Eye size={18} />
                          </button>
                          {p.status === ProposalStatus.Draft && (
                            <>
                              <button onClick={() => onEdit(p)} className="p-2 bg-slate-50 text-slate-500 rounded-lg hover:text-strivre-burgundy border border-transparent hover:border-strivre-lightnavy transition-all">
                                <Pencil size={18} />
                              </button>
                              <button onClick={() => onSend(p.id)} className="p-2 bg-slate-50 text-slate-500 rounded-lg hover:text-emerald-600 border border-transparent hover:border-strivre-lightnavy transition-all">
                                <Send size={18} />
                              </button>
                              <button onClick={() => onDelete(p.id)} className="p-2 bg-slate-50 text-slate-500 rounded-lg hover:text-red-600 border border-transparent hover:border-strivre-lightnavy transition-all">
                                <Trash2 size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      
      {/* Pagination Controls */}
      {proposals.length > 0 && (
        <div className="bg-white rounded-b-[2rem] border border-t-0 border-strivre-lightnavy px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
          <span className="text-xs md:text-sm text-slate-500 font-medium">
            Showing {paginatedData.length === 0 ? 0 : ((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, proposals.length)} of {proposals.length} proposals
          </span>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 border border-strivre-lightnavy rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm font-medium text-slate-700 min-w-[3rem] text-center">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 border border-strivre-lightnavy rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
